import React, { useState, useEffect, useCallback } from 'react';
import { V } from '../../../utils/theme';
import { Icons } from '../../../components/Icons';
import { Card, Btn, Chip, Skeleton, SuccessToastCtrl, ConfirmCtrl } from '../../../components/ui';
import { Notice, Eyebrow } from '../../../components/kit';
import { CenterModal } from '../../../components/FullSheet';
import { fmtDay, FLAG_KEYS, SCOPES } from '../../../utils/org';
import { useApi, StatusPill, memberPill, RowBtn, inputStyle, SmallLabel } from './shared';
import { memberLabel, memberCounts, flagKeysFrom, plural } from './helpers';

// ─── Members tab ───
// Paged `list_members {org_id, limit, cursor, q, role?}` → { members, next_cursor, total, counts }.
// Row actions (all with org_id): set_member_role, remove_member, assign_client / unassign_client
// (coach picker from list_coaches), and sa_view_as → read-only support view.
//
// `counts` is the org's head-count per role and is independent of the filters; `total` is
// the number of rows the *current* filter matches. The header says which is which — it
// used to show `total` under a heading that read like an org-wide member count.
const PAGE = 50;

export function MembersTab({ orgId, orgName, onChanged }) {
  const api = useApi();
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ total: 0, counts: {}, next_cursor: null });
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null); // email being acted on
  const [more, setMore] = useState(false);
  const [coaches, setCoaches] = useState(null);
  const [assignFor, setAssignFor] = useState(null); // member row
  const [viewAs, setViewAs] = useState(null); // { email, data } | { email, loading } | { email, error }

  const load = useCallback(async (opts = {}) => {
    const cursor = opts.cursor || null;
    if (cursor) setMore(true); else { setRows(null); }
    setErr('');
    const r = await api('list_members', { org_id: orgId, limit: PAGE, ...(cursor ? { cursor } : {}), ...(q.trim() ? { q: q.trim() } : {}), ...(role ? { role } : {}) });
    if (r.ok) {
      setRows(prev => (cursor ? [...(prev || []), ...(r.data.members || [])] : (r.data.members || [])));
      setMeta({ total: r.data.total ?? 0, counts: r.data.counts || {}, next_cursor: r.data.next_cursor || null });
    } else { setRows(prev => prev || []); setErr(r.msg); }
    setMore(false);
  }, [api, orgId, q, role]);

  useEffect(() => { load(); }, [orgId, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCoaches = async () => {
    if (coaches) return coaches;
    const r = await api('list_coaches', { org_id: orgId });
    const list = r.ok ? (r.data.coaches || []) : [];
    if (!r.ok) setErr(r.msg);
    setCoaches(list);
    return list;
  };

  const act = async (email, action, payload, okMsg) => {
    setBusy(email); setErr('');
    const r = await api(action, { org_id: orgId, ...payload });
    if (r.ok) { SuccessToastCtrl.show(okMsg); await load(); onChanged?.(); } else setErr(r.msg);
    setBusy(null);
    return r;
  };
  const promote = (m) => ConfirmCtrl.show(`Make ${memberLabel(m)} a coach?`, `They get the coach portal for ${orgName} and can be assigned clients.`, () => act(m.email, 'set_member_role', { email: m.email, role: 'coach' }, 'Now a coach'));
  const demote = (m) => ConfirmCtrl.show(`Make ${memberLabel(m)} a member?`, `They lose the coach portal and all ${m.client_count || 0} of their client assignments end.`, () => act(m.email, 'set_member_role', { email: m.email, role: 'member' }, 'Now a member'));
  const remove = (m) => ConfirmCtrl.show(`Remove ${memberLabel(m)} from ${orgName}?`, 'Their membership ends, consent is revoked and coach assignments close. Their data stays on their own account.', () => act(m.email, 'remove_member', { email: m.email }, 'Member removed'));
  const unassign = (m, coach) => ConfirmCtrl.show(`Unassign ${coach} from ${memberLabel(m)}?`, 'The coach loses access to this client; the message thread is kept.', () => act(m.email, 'unassign_client', { coach_email: coach, client_email: m.email }, 'Unassigned'));
  const openAssign = async (m) => { setAssignFor(m); await loadCoaches(); };
  const assign = async (coach) => {
    const m = assignFor; setAssignFor(null);
    await act(m.email, 'assign_client', { coach_email: coach, client_email: m.email }, `Assigned to ${coach}`);
  };
  const view = async (m) => {
    setViewAs({ email: m.email, loading: true });
    const r = await api('sa_view_as', { email: m.email, org_id: orgId });
    setViewAs(r.ok ? { email: m.email, data: r.data } : { email: m.email, error: r.msg });
  };

  const c = meta.counts || {};
  const totals = memberCounts(c);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="org-members">
      <Card>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} placeholder="Search name or email" aria-label="Search members"
            style={inputStyle({ flex: 1, minHeight: 40, borderRadius: 10, paddingLeft: 12, paddingRight: 12, fontSize: 14 })} />
          <Btn v="small" onClick={() => load()}>Search</Btn>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {/* Role chips read `counts` (the org's head-count per role), never the filtered
              `total` — the All chip is owner + coaches + clients, which is the same total. */}
          {[['', `All (${totals.total})`], ['owner', `Owner (${totals.owners})`], ['coach', `Coaches (${totals.coaches})`], ['member', `Clients (${totals.clients})`]].map(([v, l]) => <Chip key={v} label={l} active={role === v} onClick={() => setRole(v)} />)}
        </div>
        <div style={{ fontSize: 10, color: V.text3, marginTop: 6 }} data-testid="org-members-counts">
          {plural(totals.clients, 'client')} · {plural(totals.staff, 'staff member')} (owner + coaches) · {totals.total} active memberships
        </div>
      </Card>
      {err && <Notice tone="error">{err}</Notice>}
      {rows === null ? <Card><Skeleton rows={5} /></Card> : (
        <Card>
          <Eyebrow style={{ marginBottom: 4 }}>{rows.length} of {meta.total} matching{role || q.trim() ? ' this filter' : ''} shown</Eyebrow>
          {rows.length === 0 && <div style={{ fontSize: 12, color: V.text3, paddingTop: 6 }}>No members match.</div>}
          {rows.map(m => (
            <div key={m.email} data-testid="member-row" data-email={m.email} style={{ paddingTop: 8, paddingBottom: 8, borderTop: `1px solid ${V.divider}`, opacity: busy === m.email ? .6 : 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: m.status === 'active' ? V.text : V.text3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {m.display_name || m.name || m.email}
                    <span style={{ fontSize: 9, fontWeight: 700, color: m.role === 'owner' ? V.warn : m.role === 'coach' ? V.accent2 : V.text3, textTransform: "uppercase", letterSpacing: ".06em" }}>{m.role}</span>
                  </div>
                  <div style={{ fontSize: 10, color: V.text3, fontFamily: V.mono, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {m.email} · joined {fmtDay(m.joined_at)}{m.last_active ? ` · active ${fmtDay(m.last_active)}` : ''}{m.tags?.length ? ` · ${m.tags.join(', ')}` : ''}
                  </div>
                  {m.role === 'member' && (
                    <div style={{ fontSize: 10, color: V.text2, marginTop: 2, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {m.coach_emails?.length ? m.coach_emails.map(ce => (
                        <span key={ce} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: V.mono }}>
                          coach {ce}
                          <button type="button" onClick={() => unassign(m, ce)} aria-label={`Unassign ${ce} from ${m.email}`} disabled={busy !== null}
                            style={{ background: "none", border: "none", color: V.danger, cursor: "pointer", paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0, display: "inline-flex" }}>{Icons.x({ size: 12, color: V.danger })}</button>
                        </span>
                      )) : <span>no coach</span>}
                    </div>
                  )}
                  {m.role !== 'member' && <div style={{ fontSize: 10, color: V.text2, marginTop: 2 }}>{m.client_count || 0} client{m.client_count === 1 ? '' : 's'}</div>}
                </div>
                <StatusPill status={memberPill(m.status)} />
              </div>
              {m.status === 'active' && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                  <RowBtn onClick={() => view(m)} disabled={busy !== null} color={V.accent2}>View as</RowBtn>
                  {m.role === 'member' && <RowBtn onClick={() => openAssign(m)} disabled={busy !== null}>Assign coach</RowBtn>}
                  {m.role === 'member' && <RowBtn onClick={() => promote(m)} disabled={busy !== null}>Make coach</RowBtn>}
                  {m.role === 'coach' && <RowBtn onClick={() => demote(m)} disabled={busy !== null} color={V.warn}>Make member</RowBtn>}
                  {m.role !== 'owner' && <RowBtn onClick={() => remove(m)} disabled={busy !== null} color={V.danger}>Remove</RowBtn>}
                </div>
              )}
            </div>
          ))}
          {meta.next_cursor && <Btn v="secondary" full disabled={more} onClick={() => load({ cursor: meta.next_cursor })} s={{ marginTop: 10 }}>{more ? 'Loading…' : 'Load more'}</Btn>}
        </Card>
      )}
      {assignFor && (
        <CenterModal ariaLabel="Assign a coach" onBackdrop={() => setAssignFor(null)}>
          <div style={{ fontSize: 16, fontWeight: 800, color: V.text, marginBottom: 4 }}>Assign a coach</div>
          <div style={{ fontSize: 12, color: V.text3, marginBottom: 12, fontFamily: V.mono, wordBreak: "break-all" }}>{assignFor.email}</div>
          {coaches === null ? <Skeleton rows={3} /> : coaches.length === 0 ? <div style={{ fontSize: 12, color: V.text3 }}>No coaches in this org yet.</div> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {coaches.filter(co => !(assignFor.coach_emails || []).includes(co.email)).map(co => (
                <button key={co.email} type="button" onClick={() => assign(co.email)} data-testid="coach-option"
                  style={{ textAlign: "left", background: V.inputBg, border: `1px solid ${V.cardBorder}`, borderRadius: 10, paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12, cursor: "pointer", fontFamily: V.font }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: V.text }}>{co.name || co.email} <span style={{ fontSize: 9, color: V.text3, textTransform: "uppercase" }}>{co.role}</span></div>
                  <div style={{ fontSize: 10, color: V.text3, fontFamily: V.mono }}>{co.email}</div>
                </button>
              ))}
            </div>
          )}
          <Btn v="secondary" full onClick={() => setAssignFor(null)} s={{ marginTop: 12 }}>Cancel</Btn>
        </CenterModal>
      )}
      {viewAs && <ViewAsModal state={viewAs} onClose={() => setViewAs(null)} />}
    </div>
  );
}

// ─── View as: what `me` returns for that member. Read-only, clearly a support view. ───
// The flag chips render the keys the response carries (`keys` when the API sends one,
// otherwise the effective map plus any per-client override), not the client-side
// FLAG_KEYS constant — a flag added server-side shows up without a redeploy. FLAG_KEYS
// is only the fallback for a response that carried no flags at all.
const yesNo = (v) => (v ? 'yes' : 'no');
export function ViewAsModal({ state, onClose }) {
  const d = state.data;
  const consentRows = d?.consent ? SCOPES.map(s => [s.label, !!d.consent[`share_${s.key}`]]) : null;
  const keys = d ? flagKeysFrom(d.keys, [d.flags, d.overrides], FLAG_KEYS) : [];
  const off = keys.filter(k => d.flags?.[k] === false);
  const overridden = d ? Object.keys(d.overrides || {}).filter(k => typeof d.overrides[k] === 'boolean') : [];
  return (
    <CenterModal ariaLabel="Support view" onBackdrop={onClose}>
      <div data-testid="view-as">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: V.warn, background: `${V.warn}18`, border: `1px solid ${V.warn}40`, borderRadius: 6, paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2, textTransform: "uppercase", letterSpacing: ".08em" }}>Support view · read-only</span>
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: V.text }}>Viewing as</div>
        <div style={{ fontSize: 12, color: V.text2, fontFamily: V.mono, wordBreak: "break-all", marginBottom: 10 }}>{state.email}</div>
        {state.loading && <Skeleton rows={5} />}
        {state.error && <Notice tone="error">{state.error}</Notice>}
        {d && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Section title="Membership">
              <KV k="Org" v={d.org ? `${d.org.name} (${d.org.slug}) · ${d.org.status}` : 'none'} />
              <KV k="Role" v={d.role || '—'} />
              {d.tags?.length > 0 && <KV k="Tags" v={d.tags.join(', ')} />}
              <KV k="Coaches" v={d.coaches?.length ? d.coaches.map(co => co.name || co.email).join(', ') : 'none'} />
              <KV k="Branding" v={d.branding?.app_name ? `${d.branding.app_name} v${d.branding.version ?? '?'}` : 'IronLog default'} />
              <KV k="Powered-by badge" v={yesNo(d.powered_by)} />
            </Section>
            <Section title="Unread">
              <KV k="Messages" v={String(d.unread?.messages ?? 0)} testId="view-as-unread-messages" />
              <KV k="Announcements" v={String(d.unread?.announcements ?? 0)} testId="view-as-unread-announcements" />
            </Section>
            <Section title="Consent">
              {consentRows ? consentRows.map(([l, on]) => <KV key={l} k={l} v={on ? 'shared' : 'not shared'} color={on ? V.accent : V.text3} />) : <div style={{ fontSize: 12, color: V.text3 }}>No consent row (staff or never set).</div>}
            </Section>
            <Section title={`Effective flags · ${off.length} off of ${keys.length}`}>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {keys.map(k => {
                  const on = d.flags?.[k] !== false, ov = overridden.includes(k);
                  return <span key={k} data-testid={`view-as-flag-${k}`} data-on={String(on)} style={{ fontSize: 10, fontFamily: V.mono, color: on ? V.text2 : V.danger, background: on ? V.inputBg : `${V.danger}14`, border: `1px solid ${ov ? V.warn : 'transparent'}`, borderRadius: 6, paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2 }} title={ov ? 'per-client override' : undefined}>{k}{ov ? '*' : ''}</span>;
                })}
              </div>
              {overridden.length > 0 && <div style={{ fontSize: 10, color: V.text3, marginTop: 4 }}>* per-client override</div>}
            </Section>
            {(d.target_sessions_per_week != null || d.program) && (
              <Section title="Coaching">
                {d.target_sessions_per_week != null && <KV k="Target / week" v={`${d.sessions_this_week ?? 0} of ${d.target_sessions_per_week}`} />}
                {d.program && <KV k="Program" v={`${d.program.name} · today ${d.program.today_label}`} />}
              </Section>
            )}
          </div>
        )}
        <Btn v="secondary" full onClick={onClose} s={{ marginTop: 14 }}>Close</Btn>
      </div>
    </CenterModal>
  );
}
const Section = ({ title, children }) => (
  <div>
    <SmallLabel style={{ marginBottom: 4 }}>{title}</SmallLabel>
    <div style={{ borderRadius: 10, background: V.inputBg, paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10 }}>{children}</div>
  </div>
);
const KV = ({ k, v, color, testId }) => (
  <div style={{ display: "flex", gap: 8, justifyContent: "space-between", fontSize: 12, paddingTop: 2, paddingBottom: 2 }}>
    <span style={{ color: V.text3, flexShrink: 0 }}>{k}</span>
    <span data-testid={testId} style={{ color: color || V.text, textAlign: "right", minWidth: 0, wordBreak: "break-word" }}>{v}</span>
  </div>
);
